import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarDays, Gavel, Loader2, Mic, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { compactText, formatLegalDate } from '@/lib/legalOs';
import { loadBoardSummary } from '@/lib/workflowData';
import { AppEmptyState, AppPage, AppPageHeader, AppSectionHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';

function SurfaceCard({ title, children }) {
  return (
    <section className="advoq-card" style={{ padding: '18px', borderRadius: '24px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'grid', gap: '14px' }}>
      <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px' }}>{title}</h3>
      {children}
    </section>
  );
}

export default function HearingsPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadBoardSummary({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setSummary(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Sabha — Advoq';
    loadSummary();
  }, [loadSummary]);

  return (
    <main className="app-main" data-testid="hearings-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Sabha"
          title="Sabha hearing prep and court-day posture"
          description="Morning briefs, opening posture, bench pressure, and post-hearing recovery in one lane."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadSummary} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
          meta={<span className="app-page-chip">{summary?.dashboard?.hearing_this_week || 0} hearings this week</span>}
        />
        <WorkflowCompatibilityNotice message={loadNotice} />

        {!loading && !(summary?.morning_briefs || []).length ? (
          <AppEmptyState
            icon={CalendarDays}
            title="No hearing-facing matters are surfaced yet"
            description="As matters acquire listings, morning briefs and court-day posture will appear here."
          />
        ) : null}

        <section style={{ display: 'grid', gap: '16px' }}>
          <AppSectionHeader eyebrow="Morning brief" title="Court-facing matters" description="The matters that already need a hearing note before the board starts moving." />
          <div style={{ display: 'grid', gap: '16px' }}>
            {(summary?.morning_briefs || []).map((item) => (
              <SurfaceCard key={`${item.matter_id}_${item.hearing_date}`} title={item.matter_title}>
                <div className="app-chip-row">
                  <span className="app-page-chip">{item.court_name || 'Court to confirm'}</span>
                  <span className="app-page-chip">{formatLegalDate(item.hearing_date)}</span>
                  <span className="app-page-chip">{item.readiness_label || 'Readiness building'}</span>
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>
                  {compactText(item.summary, 220)}
                </div>
                <div className="app-chip-row">
                  {(item.next_steps || []).map((step) => (
                    <span key={step} className="app-page-chip">{step}</span>
                  ))}
                </div>
                <div className="app-chip-row">
                  <button type="button" className="app-chat-button" onClick={() => navigate(`/case/${item.matter_id}`)}><Gavel size={14} /> Hearing pack</button>
                  <button type="button" className="app-chat-button" onClick={() => navigate(`/jolly?matter_id=${encodeURIComponent(item.matter_id)}`)}><Mic size={14} /> Debrief / dictation</button>
                  <button type="button" className="app-chat-button" onClick={() => navigate(`/war-room/${item.matter_id}`)}>Open matter <ArrowRight size={14} /></button>
                </div>
              </SurfaceCard>
            ))}
          </div>
        </section>
      </AppPage>
    </main>
  );
}
